import React, { useState, useEffect, useRef } from 'react';
import { Layers, Search, Plus, Loader, Copy, WifiOff, MoreVertical, Trash2, Database, BarChart3, FileText, ShieldCheck, Share2, Shield, RefreshCw, AlertCircle, Calendar, UserCheck, X, Server, Rocket, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { getOrgSlug, getApiBaseUrl } from '../config/backend.config';
import ActivityFeedPanel from './ActivityFeedPanel';
import { featureFlags } from '../config/clerk.config';
import './WorkspaceLandingPage.css';

function WorkspaceLandingPage({ onWorkspaceSelect, onCloneSharedWorkspace, onDeleteWorkspace, refreshTrigger }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [creationError, setCreationError] = useState(null);
  const [backendUnavailable, setBackendUnavailable] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [initializingWorkspaceId, setInitializingWorkspaceId] = useState(null);
  const menuRef = useRef(null);

  // Admin state
  const [activeTab, setActiveTab] = useState('workspaces');
  const [adminWorkspaces, setAdminWorkspaces] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // System tab state
  const [systemVersions, setSystemVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState('');
  const [systemLoading, setSystemLoading] = useState(false);
  const [systemError, setSystemError] = useState(null);
  const [deployingVersion, setDeployingVersion] = useState(null);
  const [deployStatus, setDeployStatus] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});

  const { organization, membership } = useOrganization();
  const clerk = useClerk();

  const isAdmin = membership?.role === 'org:admin';

  useEffect(() => {
    fetchWorkspaces();
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenuId]);

  const fetchWorkspaces = async () => {
    try {
      setLoadingWorkspaces(true);
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) return;

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/workspaces`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setWorkspaces(data);
        setBackendUnavailable(false);
      } else if (response.status >= 500) {
        setBackendUnavailable(true);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      setBackendUnavailable(true);
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      setCreationError('Please enter a workspace name');
      return;
    }

    setCreatingWorkspace(true);
    setCreationError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) throw new Error('Unable to get authentication token');

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/workspaces`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_name: newWorkspaceName }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Creation failed: ${response.statusText}`);
      }

      const createdWorkspace = await response.json();
      setShowCreateForm(false);
      setNewWorkspaceName('');

      if (onWorkspaceSelect) {
        onWorkspaceSelect(createdWorkspace);
      }
    } catch (error) {
      setCreationError(error.message);
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const handleWorkspaceClick = async (workspace) => {
    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) throw new Error('Unable to get authentication token');

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/workspaces/${workspace.workspace_id}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch workspace: ${response.statusText}`);

      const freshWorkspace = await response.json();

      if (!freshWorkspace.is_owner) {
        if (onCloneSharedWorkspace) {
          onCloneSharedWorkspace(freshWorkspace);
        }
        return;
      }

      // Initialize workspace on server before navigating to chat
      // This ensures .claude/, utils/, config.json etc. are set up
      setInitializingWorkspaceId(workspace.workspace_id);
      try {
        const initToken = await clerk.session.getToken();
        const initResponse = await fetch(`${apiBaseUrl}/workspaces/${workspace.workspace_id}/initialize`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${initToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!initResponse.ok) {
          const errorData = await initResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to initialize workspace: ${initResponse.statusText}`);
        }
      } finally {
        setInitializingWorkspaceId(null);
      }

      if (onWorkspaceSelect) {
        onWorkspaceSelect(freshWorkspace);
      }
    } catch (error) {
      console.error('Error selecting workspace:', error);
      alert(`Failed to select workspace: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Admin functions
  const fetchAdminWorkspaces = async () => {
    setAdminLoading(true);
    setAdminError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setAdminError('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/admin/workspaces/all`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Administrator privileges required');
        }
        throw new Error(`Failed to fetch workspaces: ${response.statusText}`);
      }

      const data = await response.json();
      setAdminWorkspaces(data || []);
    } catch (err) {
      console.error('Error fetching admin workspaces:', err);
      setAdminError(err.message);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleDeleteWorkspace = async (workspaceId, workspaceName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete workspace "${workspaceName}"?\n\n` +
      `The workspace will be removed from the list. Data is retained and can be recovered if needed.`
    );

    if (!confirmed) return;

    setDeleting(workspaceId);
    setSuccessMessage(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setAdminError('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/admin/workspaces/${workspaceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete workspace: ${response.statusText}`);
      }

      setSuccessMessage(`Workspace "${workspaceName}" has been deleted.`);
      await fetchAdminWorkspaces();
    } catch (err) {
      console.error('Error deleting workspace:', err);
      alert(`Failed to delete workspace: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleTransferOwnership = async (workspace) => {
    const newOwnerUserId = window.prompt(
      `Transfer ownership of "${workspace.workspace_name}"?\n\n` +
      `Current owner: ${workspace.owner_name || workspace.owner_user_id}\n\n` +
      `Enter the new owner's User ID:`
    );

    if (!newOwnerUserId || !newOwnerUserId.trim()) {
      return;
    }

    const newOwnerName = window.prompt(
      `Enter the new owner's name (optional):`
    );

    const newOwnerEmail = window.prompt(
      `Enter the new owner's email (optional):`
    );

    const confirmed = window.confirm(
      `Confirm ownership transfer?\n\n` +
      `Workspace: ${workspace.workspace_name}\n` +
      `From: ${workspace.owner_name || workspace.owner_user_id}\n` +
      `To: ${newOwnerName || newOwnerUserId}\n\n` +
      `Note: The new owner will need to sync the workspace to access files.`
    );

    if (!confirmed) return;

    setTransferring(workspace.workspace_id);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setAdminError('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/admin/workspaces/${workspace.workspace_id}/transfer-ownership`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workspace_id: workspace.workspace_id,
          new_owner_user_id: newOwnerUserId.trim(),
          new_owner_name: newOwnerName?.trim() || '',
          new_owner_email: newOwnerEmail?.trim() || ''
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || `Failed to transfer ownership: ${response.statusText}`);
      }

      const result = await response.json();
      alert(`Success!\n\n${result.message}`);
      await fetchAdminWorkspaces();
    } catch (err) {
      console.error('Error transferring ownership:', err);
      alert(`Failed to transfer ownership:\n\n${err.message}`);
    } finally {
      setTransferring(null);
    }
  };

  // System tab functions
  const fetchSystemVersions = async () => {
    setSystemLoading(true);
    setSystemError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setSystemError('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/admin/versions`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Administrator privileges required');
        }
        throw new Error(`Failed to fetch versions: ${response.statusText}`);
      }

      const data = await response.json();
      setCurrentVersion(data.current_version || '');
      setSystemVersions(data.available_versions || []);
    } catch (err) {
      console.error('Error fetching versions:', err);
      setSystemError(err.message);
    } finally {
      setSystemLoading(false);
    }
  };

  const handleDeploy = async (version) => {
    const confirmed = window.confirm(
      `Deploy version ${version.version}?\n\n` +
      `This will update the running service to image:\n${version.image}\n\n` +
      `The service will restart and connections will be briefly interrupted.`
    );

    if (!confirmed) return;

    setDeployingVersion(version.version);
    setDeployStatus(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setDeployStatus({ success: false, message: 'Unable to get authentication token' });
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/admin/versions/deploy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          version: version.version,
          image: version.image
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Deploy failed: ${response.statusText}`);
      }

      const result = await response.json();
      setDeployStatus({ success: true, message: result.message });
    } catch (err) {
      console.error('Error deploying version:', err);
      setDeployStatus({ success: false, message: err.message });
    } finally {
      setDeployingVersion(null);
    }
  };

  const toggleNotes = (version) => {
    setExpandedNotes(prev => ({ ...prev, [version]: !prev[version] }));
  };

  const formatPublishedDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch admin workspaces when switching to admin tab
  useEffect(() => {
    if (activeTab === 'admin' && isAdmin && adminWorkspaces.length === 0) {
      fetchAdminWorkspaces();
    }
  }, [activeTab, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch system versions when switching to system tab
  useEffect(() => {
    if (activeTab === 'system' && isAdmin && systemVersions.length === 0) {
      fetchSystemVersions();
    }
  }, [activeTab, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredAdminWorkspaces = adminWorkspaces.filter(ws =>
    (ws.workspace_name || '').toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
    (ws.owner_name || '').toLowerCase().includes(adminSearchTerm.toLowerCase())
  );

  const ownedWorkspaces = workspaces.filter(ws =>
    ws.is_owner && (ws.workspace_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sharedWorkspaces = workspaces.filter(ws =>
    !ws.is_owner && (ws.workspace_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="landing-page">
      <div className="landing-layout">
        {/* Left side - Maya introduction */}
        <div className="landing-maya">
          <div className="maya-profile">
            <img src="/maya.jpg" alt="Maya" className="maya-avatar" />
            <h2 className="maya-name">Meet Maya</h2>
            <p className="maya-role">Your Code-Enabled Business Analyst</p>
          </div>
          <p className="maya-intro">
            Maya is an AI analyst that works directly with your data. She writes and executes real code against your databases, so every answer is grounded in actual query results — not guesses or hallucinations.
          </p>
          <div className="maya-capabilities">
            <div className="maya-capability">
              <div className="maya-capability-icon">
                <Database size={18} />
              </div>
              <div>
                <span className="maya-capability-title">Complex Business Queries</span>
                <span className="maya-capability-desc">Go beyond SQL joins — Maya writes Python scripts to handle multi-step logic, conditional aggregations, and cross-dataset analysis.</span>
              </div>
            </div>
            <div className="maya-capability">
              <div className="maya-capability-icon">
                <BarChart3 size={18} />
              </div>
              <div>
                <span className="maya-capability-title">Business Intelligence</span>
                <span className="maya-capability-desc">Dive into your data with charts, trend analysis, and actionable insights — all generated from live queries.</span>
              </div>
            </div>
            <div className="maya-capability">
              <div className="maya-capability-icon">
                <FileText size={18} />
              </div>
              <div>
                <span className="maya-capability-title">Report Generation</span>
                <span className="maya-capability-desc">Save analyses as reusable reports that re-run from scratch, ensuring fresh data every time.</span>
              </div>
            </div>
            <div className="maya-capability">
              <div className="maya-capability-icon">
                <ShieldCheck size={18} />
              </div>
              <div>
                <span className="maya-capability-title">No Hallucinations</span>
                <span className="maya-capability-desc">Every answer is backed by executed code and real query results. Maya shows her work — you see the data, not fabricated numbers.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Workspaces */}
        <div className="landing-container">
          <div className="landing-header">
            <h1>
              <Layers size={28} />
              Workspaces
            </h1>
            {isAdmin && (
              <div className="landing-tabs">
                <button
                  className={`landing-tab${activeTab === 'workspaces' ? ' landing-tab-active' : ''}`}
                  onClick={() => setActiveTab('workspaces')}
                >
                  <Layers size={16} />
                  My Workspaces
                </button>
                <button
                  className={`landing-tab${activeTab === 'admin' ? ' landing-tab-active' : ''}`}
                  onClick={() => setActiveTab('admin')}
                >
                  <Shield size={16} />
                  Admin
                </button>
                <button
                  className={`landing-tab${activeTab === 'system' ? ' landing-tab-active' : ''}`}
                  onClick={() => setActiveTab('system')}
                >
                  <Server size={16} />
                  System
                </button>
              </div>
            )}
            {activeTab === 'workspaces' && (
              <p>Select a workspace to get started, or create a new one.</p>
            )}
            {activeTab === 'admin' && (
              <p>Manage all workspaces in your organization.</p>
            )}
            {activeTab === 'system' && (
              <p>View available versions and deploy updates.</p>
            )}
          </div>

          {/* Create workspace - only show on workspaces tab */}
          {activeTab === 'workspaces' && (
          <div className="landing-actions">
            {!showCreateForm ? (
              <button
                className="landing-create-button"
                onClick={() => setShowCreateForm(true)}
              >
                <Plus size={18} />
                Create New Workspace
              </button>
            ) : (
              <div className="landing-create-form">
                <input
                  type="text"
                  placeholder="Enter workspace name..."
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  className="landing-create-input"
                  disabled={creatingWorkspace}
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newWorkspaceName.trim()) handleCreateWorkspace();
                    else if (e.key === 'Escape') {
                      setShowCreateForm(false);
                      setNewWorkspaceName('');
                      setCreationError(null);
                    }
                  }}
                />
                <button
                  className="landing-create-confirm"
                  onClick={handleCreateWorkspace}
                  disabled={creatingWorkspace || !newWorkspaceName.trim()}
                >
                  {creatingWorkspace ? <><Loader size={14} className="spinner" /> Creating...</> : 'Create'}
                </button>
                <button
                  className="landing-create-cancel"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewWorkspaceName('');
                    setCreationError(null);
                  }}
                  disabled={creatingWorkspace}
                >
                  Cancel
                </button>
                {creationError && <div className="landing-error">{creationError}</div>}
              </div>
            )}
          </div>
          )}

          {/* Content - My Workspaces Tab */}
          {activeTab === 'workspaces' && (
          <>
          {backendUnavailable ? (
            <div className="landing-unavailable">
              <WifiOff size={48} />
              <h2>Service Unavailable</h2>
              <p>Unable to connect to the server. Please try again later.</p>
              <button
                className="landing-retry-button"
                onClick={() => { setBackendUnavailable(false); fetchWorkspaces(); }}
              >
                Retry
              </button>
            </div>
          ) : loadingWorkspaces ? (
            <div className="landing-loading">
              <div className="loading-spinner"></div>
              <span>Loading workspaces...</span>
            </div>
          ) : (
            <div className="landing-sections">
              {/* My Workspaces */}
              <div className="landing-section">
                <h2>My Workspaces</h2>
                {workspaces.filter(ws => ws.is_owner).length > 20 && (
                  <div className="landing-search">
                    <Search size={16} />
                    <input
                      type="text"
                      placeholder="Search workspaces..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                )}
                {ownedWorkspaces.length === 0 ? (
                  <div className="landing-empty">
                    <p>No workspaces yet. Create one to get started.</p>
                  </div>
                ) : (
                  <div className="landing-grid">
                    {ownedWorkspaces.map((ws) => (
                      <div
                        key={ws.workspace_id}
                        className={`landing-card${ws.is_shared ? ' landing-card-is-shared' : ''}${initializingWorkspaceId === ws.workspace_id ? ' landing-card-initializing' : ''}`}
                        onClick={() => !initializingWorkspaceId && handleWorkspaceClick(ws)}
                      >
                        <div className="landing-card-row">
                          <div className="landing-card-icon">
                            {initializingWorkspaceId === ws.workspace_id ? (
                              <Loader size={20} className="spinner" />
                            ) : (
                              <Layers size={20} />
                            )}
                          </div>
                          <div className="landing-card-info">
                            <span className="landing-card-name" title={ws.workspace_name}>{ws.workspace_name}</span>
                            <span className="landing-card-date">
                              {initializingWorkspaceId === ws.workspace_id ? 'Preparing workspace...' : formatDate(ws.created_at)}
                            </span>
                          </div>
                          {onDeleteWorkspace && (
                            <div className="landing-card-menu" ref={openMenuId === ws.workspace_id ? menuRef : undefined}>
                              <button
                                className="landing-card-menu-trigger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openMenuId === ws.workspace_id) {
                                    setOpenMenuId(null);
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setMenuPos({ top: rect.bottom + 4, left: rect.right - 120 });
                                    setOpenMenuId(ws.workspace_id);
                                  }
                                }}
                              >
                                <MoreVertical size={16} />
                              </button>
                              {openMenuId === ws.workspace_id && (
                                <div className="landing-card-menu-dropdown" style={{ top: menuPos.top, left: menuPos.left }}>
                                  <button
                                    className="landing-card-menu-item landing-card-menu-item-danger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuId(null);
                                      onDeleteWorkspace(ws);
                                    }}
                                  >
                                    <Trash2 size={14} />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {ws.is_shared && (
                          <div className="landing-shared-strip">
                            <Share2 size={10} />
                            Shared with organization
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Shared Workspaces */}
              {sharedWorkspaces.length > 0 && (
                <div className="landing-section">
                  <h2>Available to Clone</h2>
                  <p className="landing-section-desc">
                    Clone a shared workspace to create your own editable copy.
                  </p>
                  <div className="landing-grid">
                    {sharedWorkspaces.map((ws) => (
                      <div
                        key={ws.workspace_id}
                        className="landing-card landing-card-shared"
                        onClick={() => handleWorkspaceClick(ws)}
                      >
                        <div className="landing-card-row">
                          <div className="landing-card-icon shared">
                            <Copy size={20} />
                          </div>
                          <div className="landing-card-info">
                            <span className="landing-card-name" title={ws.workspace_name}>{ws.workspace_name}</span>
                            <span className="landing-card-owner">by {ws.owner_name}</span>
                            <span className="landing-card-date">{formatDate(ws.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </>
          )}

          {/* Content - Admin Tab */}
          {activeTab === 'admin' && isAdmin && (
            <div className="admin-content">
              {/* Admin Controls */}
              <div className="admin-controls">
                <div className="admin-info-banner">
                  <AlertCircle size={14} />
                  <span>Administrator view — showing all workspaces in organization</span>
                </div>
                <div className="admin-controls-right">
                  {adminWorkspaces.length > 5 && (
                    <div className="landing-search">
                      <Search size={16} />
                      <input
                        type="text"
                        placeholder="Search workspaces or owners..."
                        value={adminSearchTerm}
                        onChange={(e) => setAdminSearchTerm(e.target.value)}
                      />
                    </div>
                  )}
                  <button
                    className="admin-refresh-button"
                    onClick={fetchAdminWorkspaces}
                    disabled={adminLoading}
                    title="Refresh workspaces"
                  >
                    <RefreshCw size={14} className={adminLoading ? 'spinner' : ''} />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>

              {/* Success Message */}
              {successMessage && (
                <div className="admin-success-banner">
                  <span>{successMessage}</span>
                  <button className="admin-success-dismiss" onClick={() => setSuccessMessage(null)}>
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Admin Content */}
              {adminLoading ? (
                <div className="landing-loading">
                  <RefreshCw size={24} className="spinner" />
                  <span>Loading workspaces...</span>
                </div>
              ) : adminError ? (
                <div className="admin-error-state">
                  <AlertCircle size={32} />
                  <span>{adminError}</span>
                  <button onClick={fetchAdminWorkspaces} className="landing-retry-button">
                    Try Again
                  </button>
                </div>
              ) : filteredAdminWorkspaces.length === 0 ? (
                <div className="landing-empty">
                  <p>{adminSearchTerm ? 'No workspaces match your search.' : 'No workspaces found in this organization.'}</p>
                </div>
              ) : (
                <div className="admin-workspaces-list">
                  {/* Header */}
                  <div className="admin-list-header">
                    <div className="admin-header-name">Workspace</div>
                    <div className="admin-header-owner">Owner</div>
                    <div className="admin-header-actions">Actions</div>
                  </div>

                  {/* Workspace Rows */}
                  <div className="admin-list-body">
                    {filteredAdminWorkspaces.map((workspace) => (
                      <div key={workspace.workspace_id} className="admin-workspace-row">
                        <div className="admin-row-name">
                          <div className="workspace-name-with-badge">
                            <strong>{workspace.workspace_name}</strong>
                            {workspace.is_shared && (
                              <span className="admin-shared-badge">Shared</span>
                            )}
                          </div>
                          <div className="workspace-details">
                            <span className="detail-item">
                              <Calendar size={11} />
                              {formatDateTime(workspace.updated_at)}
                            </span>
                          </div>
                          <span className="admin-workspace-id">{workspace.workspace_id}</span>
                        </div>
                        <div className="admin-row-owner">
                          <div className="owner-name">{workspace.owner_name || 'Unknown'}</div>
                          <div className="owner-id">{workspace.owner_user_id}</div>
                        </div>
                        <div className="admin-row-actions">
                          <button
                            className="admin-transfer-button"
                            onClick={() => handleTransferOwnership(workspace)}
                            disabled={transferring === workspace.workspace_id || deleting === workspace.workspace_id}
                            title="Transfer ownership"
                          >
                            {transferring === workspace.workspace_id ? (
                              <RefreshCw size={14} className="spinner" />
                            ) : (
                              <UserCheck size={14} />
                            )}
                          </button>
                          <button
                            className="admin-delete-button"
                            onClick={() => handleDeleteWorkspace(workspace.workspace_id, workspace.workspace_name)}
                            disabled={deleting === workspace.workspace_id || transferring === workspace.workspace_id}
                            title="Delete workspace"
                          >
                            {deleting === workspace.workspace_id ? (
                              <RefreshCw size={14} className="spinner" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="admin-list-footer">
                    <span>Total workspaces: {filteredAdminWorkspaces.length}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Content - System Tab */}
          {activeTab === 'system' && isAdmin && (
            <div className="admin-content">
              <div className="admin-controls">
                <div className="admin-info-banner">
                  <Server size={14} />
                  <span>Version management — deploy updates to the running service</span>
                </div>
                <div className="admin-controls-right">
                  <button
                    className="admin-refresh-button"
                    onClick={fetchSystemVersions}
                    disabled={systemLoading}
                    title="Refresh versions"
                  >
                    <RefreshCw size={14} className={systemLoading ? 'spinner' : ''} />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>

              {/* Deploy Status */}
              {deployStatus && (
                <div className={deployStatus.success ? 'admin-success-banner' : 'system-error-banner'}>
                  <span>{deployStatus.message}</span>
                  <button className="admin-success-dismiss" onClick={() => setDeployStatus(null)}>
                    <X size={14} />
                  </button>
                </div>
              )}

              {systemLoading ? (
                <div className="landing-loading">
                  <RefreshCw size={24} className="spinner" />
                  <span>Loading versions...</span>
                </div>
              ) : systemError ? (
                <div className="admin-error-state">
                  <AlertCircle size={32} />
                  <span>{systemError}</span>
                  <button onClick={fetchSystemVersions} className="landing-retry-button">
                    Try Again
                  </button>
                </div>
              ) : (
                <>
                  {/* Current Version */}
                  <div className="system-current-version">
                    <Server size={16} />
                    <span>Running version: <strong>{currentVersion || 'Unknown'}</strong></span>
                  </div>

                  {/* Versions List */}
                  {systemVersions.length === 0 ? (
                    <div className="landing-empty">
                      <p>No published versions found in the registry.</p>
                    </div>
                  ) : (
                    <div className="system-versions-list">
                      {systemVersions.map((v) => {
                        const isCurrent = v.version === currentVersion;
                        const isDeploying = deployingVersion === v.version;
                        return (
                          <div key={v.version} className={`system-version-card${isCurrent ? ' system-version-current' : ''}`}>
                            <div className="system-version-header">
                              <div className="system-version-info">
                                <div className="system-version-title">
                                  <span className="system-version-number">v{v.version}</span>
                                  {isCurrent && (
                                    <span className="system-current-badge">
                                      <CheckCircle size={12} />
                                      Current
                                    </span>
                                  )}
                                </div>
                                <span className="system-version-date">
                                  <Calendar size={11} />
                                  {formatPublishedDate(v.published_at)}
                                </span>
                              </div>
                              <div className="system-version-actions">
                                {isCurrent ? (
                                  <span className="system-running-label">Running</span>
                                ) : (
                                  <button
                                    className="system-deploy-button"
                                    onClick={() => handleDeploy(v)}
                                    disabled={!!deployingVersion}
                                  >
                                    {isDeploying ? (
                                      <><RefreshCw size={14} className="spinner" /> Deploying...</>
                                    ) : (
                                      <><Rocket size={14} /> Deploy</>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                            {v.notes && (
                              <div className="system-version-notes">
                                <button
                                  className="system-notes-toggle"
                                  onClick={() => toggleNotes(v.version)}
                                >
                                  {expandedNotes[v.version] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  Release notes
                                </button>
                                {expandedNotes[v.version] && (
                                  <div className="system-notes-content">
                                    {v.notes.split('\n').map((line, i) => (
                                      <div key={i}>{line || '\u00A0'}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="system-deploy-warning">
                    <AlertCircle size={14} />
                    <span>Deploying a version will restart the service and briefly interrupt active connections.</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {featureFlags.comingSoon && <ActivityFeedPanel />}
      </div>
    </div>
  );
}

export default WorkspaceLandingPage;
